import { useCallback, useEffect, useRef, useState } from "react";
import { ContentPreview, type ResponseType } from "./ContentPreview.js";
import { ContentReviewSection } from "./ContentReviewSection.js";
import { usePdfBlob } from "./usePdfBlob.js";
import { useStickyState } from "./useStickyState.js";

export interface ContentSource {
  id: number;
  filename: string;
  roleTag?: string;
}

function roleLabel(tag?: string): string {
  if (tag === "BOOK_BACK_QUESTIONS") return "Book-Back Questions";
  if (tag === "CHAPTER_GUIDE") return "Chapter Guide";
  return "Textbook";
}

function roleBadgeClass(tag?: string): string {
  if (tag === "BOOK_BACK_QUESTIONS") return "badge-bbq";
  if (tag === "CHAPTER_GUIDE") return "badge-guide";
  return "badge-textbook";
}

/** Which pane fills the width; "split" shows both. */
type Pane = "split" | "pdf" | "content";

/**
 * Generated content beside the PDF that grounded it, so a reviewer can check a
 * claim against its source without losing their place in either.
 *
 * A split pane rather than a PDF modal stacked over the content modal:
 * PdfViewerModal owns its own Escape handler and a fullscreen sub-state, so
 * layering the two puts two competing key handlers and z-index stacks on
 * screen at once.
 *
 * A run can be grounded on several PDFs — a textbook, the book-back questions,
 * a chapter guide — so the left pane is a source picker, not a single embed.
 * With no sources at all (retrieval disabled, or a run from before executions
 * recorded them) it degrades to the content alone rather than framing an error.
 */
export function SourcedContentView({
  collectionId,
  sources,
  content,
  responseType,
  questionAssessmentType,
  review,
}: {
  collectionId: number | null;
  sources: ContentSource[];
  content: string;
  responseType: ResponseType;
  questionAssessmentType?: string | null;
  /** Set only when this content is backed by a generated_contents row — e.g.
   *  a pipeline execution that finished and produced one. Turns the plain
   *  read-only preview into the same edit/diff/reviewer-status UI the
   *  Generation Queue uses, so a reviewer does not need to leave this
   *  side-by-side PDF view to edit or record a verdict. */
  review?: {
    contentId: number;
    generatedContent: string;
    reviewedContent: string | null;
    toast: (m: string) => void;
    onSaved: (reviewedContent: string | null) => void;
    onApproved?: () => void;
  };
}) {
  const [activeSourceId, setActiveSourceId] = useState<number | null>(sources[0]?.id ?? null);
  // Which pane a reviewer prefers survives across opens the same way splitPct
  // does — re-picking "Content only" every single time they open this view
  // is exactly the kind of friction useStickyState exists to remove.
  const [pane, setPane] = useStickyState<Pane>("zf.sourcedContent.pane", "split");
  // The reviewer's preferred balance survives closing the modal: they re-open
  // this view dozens of times a session and re-dragging each time is friction.
  const [splitPct, setSplitPct] = useStickyState<number>("zf.sourcedContent.splitPct", 50);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const activeSource = sources.find((s) => s.id === activeSourceId) ?? null;
  const { blobUrl, loadError } = usePdfBlob(collectionId, activeSource?.id ?? null);

  // Keep the selection valid when the run being viewed changes underneath us.
  useEffect(() => {
    if (sources.length === 0) {
      setActiveSourceId(null);
      return;
    }
    setActiveSourceId((current) =>
      current !== null && sources.some((s) => s.id === current) ? current : (sources[0]?.id ?? null),
    );
  }, [sources]);

  const onDragMove = useCallback(
    (clientX: number) => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const pct = ((clientX - rect.left) / rect.width) * 100;
      // Neither pane may be dragged away entirely; the collapse buttons are
      // the way to get a full-width view.
      setSplitPct(Math.min(80, Math.max(20, pct)));
    },
    [setSplitPct],
  );

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current) return;
      // The iframe swallows mousemove once the cursor crosses into it, so the
      // drag is tracked on window and text selection suppressed while it runs.
      e.preventDefault();
      onDragMove(e.clientX);
    }
    function onUp() {
      draggingRef.current = false;
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
  }, [onDragMove]);

  const contentPane = (
    <div style={{ minWidth: 0, overflow: "auto" }}>
      {review ? (
        <ContentReviewSection
          contentId={review.contentId}
          generatedContent={review.generatedContent}
          reviewedContent={review.reviewedContent}
          responseType={responseType}
          toast={review.toast}
          onSaved={review.onSaved}
          onApproved={review.onApproved}
        />
      ) : (
        <ContentPreview
          content={content}
          responseType={responseType}
          questionAssessmentType={questionAssessmentType}
        />
      )}
    </div>
  );

  if (sources.length === 0 || collectionId == null) {
    return contentPane;
  }

  const showPdf = pane !== "content";
  const showContent = pane !== "pdf";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        // Fills whatever height the parent gives it (a normal modal's content
        // area, or the full viewport in fullscreen) rather than a fixed
        // vh figure — a fixed height either wasted the room fullscreen freed
        // up or clipped the panes on a short window.
        flex: "1 1 auto",
        minHeight: 0,
        background: "var(--panel)",
      }}
    >
      {/* Source picker + pane controls */}
      <div
        className="row between"
        style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}
      >
        <div className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span className="mini muted" style={{ fontWeight: 700 }}>
            Source
          </span>
          {sources.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`btn mini ${s.id === activeSourceId ? "primary" : ""}`}
              onClick={() => setActiveSourceId(s.id)}
              title={s.filename}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, maxWidth: 260 }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {s.filename}
              </span>
              <span className={`badge mini ${roleBadgeClass(s.roleTag)}`}>{roleLabel(s.roleTag)}</span>
            </button>
          ))}
        </div>

        <div className="row" style={{ gap: 4 }}>
          <button
            type="button"
            className={`btn mini ${pane === "pdf" ? "primary" : ""}`}
            onClick={() => setPane(pane === "pdf" ? "split" : "pdf")}
            title="Show only the source PDF"
          >
            ◧ PDF
          </button>
          <button
            type="button"
            className={`btn mini ${pane === "split" ? "primary" : ""}`}
            onClick={() => setPane("split")}
            title="Show both side by side"
          >
            ◫ Split
          </button>
          <button
            type="button"
            className={`btn mini ${pane === "content" ? "primary" : ""}`}
            onClick={() => setPane(pane === "content" ? "split" : "content")}
            title="Show only the generated content"
          >
            ◨ Content
          </button>
          {blobUrl && (
            <a
              className="btn mini"
              href={blobUrl}
              target="_blank"
              rel="noreferrer"
              title="Open this PDF in a new browser tab"
            >
              ↗ New Tab
            </a>
          )}
        </div>
      </div>

      <div
        ref={wrapRef}
        style={{
          display: "grid",
          gridTemplateColumns: showPdf && showContent ? `${splitPct}% 8px 1fr` : "1fr",
          // Grows to fill whatever the flex parent above gives it. A modal
          // that isn't in a flex/fixed-height ancestor (nothing constrains
          // its own height) falls back to the min so the panes never collapse
          // to zero.
          flex: "1 1 auto",
          minHeight: "50vh",
          border: "1px solid var(--line)",
          borderRadius: 8,
          overflow: "hidden",
          background: "var(--panel)",
        }}
      >
        {showPdf && (
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", background: "var(--bg)" }}>
            {loadError ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--red)",
                  padding: 24,
                  textAlign: "center",
                }}
              >
                {loadError}
              </div>
            ) : !blobUrl ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--muted)",
                }}
              >
                Loading PDF…
              </div>
            ) : (
              <iframe
                src={blobUrl}
                title={`Source PDF — ${activeSource?.filename ?? ""}`}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            )}
          </div>
        )}

        {showPdf && showContent && (
          <div
            role="separator"
            aria-orientation="vertical"
            onMouseDown={(e) => {
              e.preventDefault();
              draggingRef.current = true;
              document.body.style.userSelect = "none";
            }}
            onDoubleClick={() => setSplitPct(50)}
            title="Drag to resize · double-click to reset"
            style={{
              cursor: "col-resize",
              background: "var(--line)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 10, color: "var(--muted)", pointerEvents: "none" }}>⋮</span>
          </div>
        )}

        {showContent && (
          <div style={{ minWidth: 0, overflow: "auto", padding: 12, background: "var(--panel)" }}>
            {review ? (
              <ContentReviewSection
                contentId={review.contentId}
                generatedContent={review.generatedContent}
                reviewedContent={review.reviewedContent}
                responseType={responseType}
                toast={review.toast}
                onSaved={review.onSaved}
                onApproved={review.onApproved}
              />
            ) : (
              <ContentPreview
                content={content}
                responseType={responseType}
                questionAssessmentType={questionAssessmentType}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
